import React, { useMemo, useCallback } from 'react';
import { Box, Typography, Grid, TextField, Button, Paper, Checkbox, FormControlLabel } from '@mui/material';
import moment from 'moment-timezone';
import { ShiftRequest } from 'types/shift-requests';
import { useQuery } from 'react-query';
import { getDays } from 'core/API/organizationSettings';
import { getTimeOffReasons } from 'core/API/scheduler';

interface RequestDetailViewProps {
	request: ShiftRequest;
	onApprove: () => void;
	onDeny: () => void;
	onClose: () => void;
	canManageRequests?: boolean;
	allShifts?: any[]; // All shifts from schedule page
}

const RequestDetailView: React.FC<RequestDetailViewProps> = ({
	request,
	onApprove,
	onDeny,
	onClose,
	canManageRequests = false,
	allShifts = [],
}) => {
	const requestType = request.type?.label?.toLowerCase() || '';
	
	// Find shift details from allShifts by matching @id
	const findShiftDetails = useCallback((shiftRequest: any): any => {
		if (!shiftRequest) return null;
		
		const shiftId = shiftRequest['@id'];
		if (!shiftId || !allShifts) return shiftRequest;
		
		const foundShift = allShifts.find((shift: any) => shift['@id'] === shiftId);
		return foundShift || shiftRequest;
	}, [allShifts]);

	// Use full shift data if found, otherwise use request data
	const enrichedShiftRequest = useMemo(() => {
		return findShiftDetails(request.shiftRequest);
	}, [request.shiftRequest, findShiftDetails]);

	const enrichedShiftAssigned = useMemo(() => {
		return findShiftDetails(request.shiftAssigned);
	}, [request.shiftAssigned, findShiftDetails]);
	
	// Fetch days for availability display
	const { data: daysData = [] } = useQuery(['days'], () => getDays());

	// Fetch time-off reasons
	const { data: timeOffReasonsData = [] } = useQuery(['time-off-reasons'], () => getTimeOffReasons());

	// Find time-off reason name
	const getTimeOffReasonName = (reasonData: any): string => {
		if (!reasonData) return 'N/A';
		
		// If it's an object with @id, extract the @id
		let reasonIri: string | null = null;
		if (typeof reasonData === 'object' && reasonData['@id']) {
			reasonIri = reasonData['@id'];
		} else if (typeof reasonData === 'string') {
			reasonIri = reasonData;
		}
		
		if (!reasonIri) return 'N/A';
		
		const reason = timeOffReasonsData.find((r: any) => r['@id'] === reasonIri);
		return reason?.name || reasonIri;
	};

	// Format shift display
	const formatShiftDisplay = (shift: any) => {
		if (!shift) return 'N/A';
		// console.log("Formatting shift:", shift);
		const startTime = shift.startAt ? moment(shift.startAt).format('ddd, MMM D - h:mm A') : '';
		const endTime = shift.endAt ? moment(shift.endAt).format('h:mm A') : '';
		const jobCode = shift.jobCode?.label ? ` | ${shift.jobCode.label}` : '';
		
		if (startTime && endTime) {
			return `${startTime} - ${endTime}${jobCode}`;
		}
		return 'N/A';
	};

	// Get employee name
	const getEmployeeName = () => {
		// Try to extract name from employeeRequest object
		if (request.employeeRequest) {
			const empReq = request.employeeRequest as any;
			// If it's an object with firstName/lastName

			return `${empReq.firstName} ${empReq.lastName || ''}`.trim();
		} else if (request.createdBy) {
			const creator = request.createdBy as any;
			return `${creator.firstName || ''} ${creator.lastName || ''}`.trim();
		}
		// Fallback to createdBy
		return request.createdBy || 'Unknown Employee';
	};

	// Get request details based on type
	const renderRequestDetails = () => {
		if (requestType.includes('time off') || requestType.includes('time-off')) {
			return (
				<>
					<Grid item xs={12} sm={6}>
						<TextField
							fullWidth
							type="date"
							label="Start Date"
							value={request.startAt ? moment(`${request.startAt}.000Z`).format('YYYY-MM-DD') : ''}
							InputProps={{ readOnly: true }}
							InputLabelProps={{ shrink: true }}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							fullWidth
							type="date"
							label="End Date"
							value={request.endAt ? moment(`${request.endAt}.000Z`).format('YYYY-MM-DD') : ''}
							InputProps={{ readOnly: true }}
							InputLabelProps={{ shrink: true }}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Reason"
							value={getTimeOffReasonName((request as any).timeOffReasons || (request as any).reason)}
							InputProps={{ readOnly: true }}
						/>
					</Grid>
					{(request).reasonDescription && (
						<Grid item xs={12}>
							<TextField
								fullWidth
								multiline
								rows={2}
								label="Reason Description"
								value={(request).reasonDescription}
								InputProps={{ readOnly: true }}
							/>
						</Grid>
					)}
					{(request).reasonNote && (
						<Grid item xs={12}>
							<TextField
								fullWidth
								multiline
								rows={2}
								label="Reason Note"
								value={(request).reasonNote}
								InputProps={{ readOnly: true }}
							/>
						</Grid>
					)}
				</>
			);
		}

		if (requestType.includes('availability')) {
			return (
				<>
					<Grid item xs={12}>
						<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
							Availability Schedule
						</Typography>
						{daysData.map((day: any) => {
							const isDaySelected = request.day === day['@id'];
							
							return (
								<Grid
									container
									spacing={2}
									alignItems="center"
									key={day['@id']}
									sx={{
										mb: 1,
										borderRadius: 1,
										p: 1,
										backgroundColor: isDaySelected ? '#f0f4f8' : 'transparent',
									}}
								>
									<Grid item xs={4}>
										<FormControlLabel
											control={<Checkbox checked={isDaySelected} disabled />}
											label={day.label}
										/>
									</Grid>
									<Grid item xs={4}>
										<TextField
											fullWidth
											label="Start Time"
										value={isDaySelected && request.startAt ? moment(request.startAt).format('h:mm A') : ''}
										InputProps={{ readOnly: true }}
										disabled={!isDaySelected}
									/>
								</Grid>
								<Grid item xs={4}>
									<TextField
										fullWidth
										label="End Time"
										value={isDaySelected && request.endAt ? moment(request.endAt).format('h:mm A') : ''}
											InputProps={{ readOnly: true }}
											disabled={!isDaySelected}
										/>
									</Grid>
								</Grid>
							);
						})}
					</Grid>
				</>
			);
		}

		if (requestType.includes('cover')) {
			return (
				<>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Shift to Cover"
							value={formatShiftDisplay(enrichedShiftRequest)}
							InputProps={{ readOnly: true }}
						/>
					</Grid>
					{request.employeeAssigned && (
						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Requested Coverage Employee"
								value={`${(request.employeeAssigned as any).firstName || ''} ${(request.employeeAssigned as any).lastName || ''}`.trim() || 'Unknown'}
								InputProps={{ readOnly: true }}
							/>
						</Grid>
					)}
					{request.reason && (
						<Grid item xs={12}>
							<TextField
								fullWidth
								multiline
								rows={2}
								label="Reason"
								value={request.reason}
								InputProps={{ readOnly: true }}
							/>
						</Grid>
					)}
				</>
			);
		}

		if (requestType.includes('trade')) {
			return (
				<>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="My Shift"
							value={formatShiftDisplay(enrichedShiftRequest)}
							InputProps={{ readOnly: true }}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Trade With Employee"
							value={`${(request.employeeAssigned as any).firstName || ''} ${(request.employeeAssigned as any).lastName || ''}`.trim() || 'Unknown'}
							InputProps={{ readOnly: true }}
						/>
					</Grid>
					{enrichedShiftAssigned && (
						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Their Shift"
								value={formatShiftDisplay(enrichedShiftAssigned)}
								InputProps={{ readOnly: true }}
							/>
						</Grid>
					)}
				</>
			);
		}

		if (requestType.includes('pick') || requestType.includes('pickup')) {
			return (
				<>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Shift"
							value={formatShiftDisplay(enrichedShiftRequest)}
							InputProps={{ readOnly: true }}
						/>
					</Grid>
					{request.reason && (
						<Grid item xs={12}>
							<TextField
								fullWidth
								multiline
								rows={2}
								label="Reason"
								value={request.reason}
								InputProps={{ readOnly: true }}
							/>
						</Grid>
					)}
				</>
			);
		}

		return null;
	};

	return (
		<Paper sx={{ minHeight: '100vh', py: 3, borderRadius: 3, boxShadow: 2 }}>
			<Box maxWidth="1100px" mx="auto" px={3}>
				{/* Header */}
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
					<Button
						onClick={onClose}
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							textTransform: 'none',
							fontWeight: 500,
							px: 3,
							py: 0.75,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
					>
					Back
				</Button>
				<Typography variant="h5" sx={{ fontWeight: 600 }}>
					{request.type?.label || 'Unknown'} Request
				</Typography>
				{canManageRequests ? (
						<Box sx={{ display: 'flex', gap: 2 }}>
							<Button
								onClick={onDeny}
								sx={{
									backgroundColor: '#dc3545',
									color: '#fff',
									textTransform: 'none',
									fontWeight: 500,
									px: 3,
									py: 0.75,
									'&:hover': { backgroundColor: '#c82333' },
								}}
							>
								Deny
							</Button>
							<Button
								onClick={onApprove}
								sx={{
									backgroundColor: '#4caf50',
									color: '#fff',
									textTransform: 'none',
									fontWeight: 500,
									px: 3,
									py: 0.75,
									'&:hover': { backgroundColor: '#45a049' },
								}}
							>
								Approve
							</Button>
						</Box>
					) : (
						<Box sx={{ width: '240px' }} />
					)}
				</Box>

				{/* Request Details */}
				<Box sx={{ p: 4 }}>
					<Typography variant="h6" gutterBottom>
						Request Details
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						Review request information
					</Typography>

					<Grid container spacing={3}>
						{/* Employee Information */}
						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Employee"
								value={getEmployeeName()}
								InputProps={{ readOnly: true }}
							/>
						</Grid>

						{/* Request Date */}
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								label="Request Date"
								value={
									request.created_at ? moment(request.created_at).format('MM/DD/YYYY h:mm A') : ''
								}
								InputProps={{ readOnly: true }}
							/>
						</Grid>

					{/* Request Type */}
					<Grid item xs={12} sm={6}>
						<TextField
							fullWidth
							label="Request Type"
							value={request.type?.label || 'Unknown'}
							InputProps={{ readOnly: true }}
						/>
					</Grid>

					{/* Type-specific details */}
					{renderRequestDetails()}						{/* Notes */}
						{request.note && (
							<Grid item xs={12}>
								<TextField
									fullWidth
									multiline
									rows={3}
									label="Notes"
									value={request.note}
									InputProps={{ readOnly: true }}
								/>
							</Grid>
						)}
					</Grid>
				</Box>
			</Box>
		</Paper>
	);
};

export default RequestDetailView;
